import { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import * as React from 'react'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineShieldSlashIcon from 'LuxKit/Icons/line/LineShieldSlashIcon'
import SolidShieldCheckIcon from 'LuxKit/Icons/solid/SolidShieldCheckIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import type { PropertyCancellationLabelShape } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import { Refundable } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import Clickable from 'components/Common/Clickable'
import ModalContext from 'contexts/ModalContext'
import CancellationPolicyModal from 'components/Common/CancellationPolicies/CancellationPolicyModal'
import { getPropertyCancellationLabelText } from './propertyCancellationText'

export interface PropertyCancellationPolicyViewProps {
  refundableStatus: Refundable
  refundDescription: string
  labelShape: PropertyCancellationLabelShape
  policyDescriptions: Array<string | undefined>
  /** Set when the policy reads as a timeline; the date the label shows. */
  timelineDeadline?: string
}

const RENDER_INFO_ICON = () => <LineInfoCircleIcon size="XS" />

function PropertyCancellationPolicyView({
  refundableStatus,
  refundDescription,
  labelShape,
  policyDescriptions,
  timelineDeadline,
}: PropertyCancellationPolicyViewProps) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const openModal = useCallback(() => {
    showModal(<CancellationPolicyModal
      heading={refundDescription}
      timelineDeadline={timelineDeadline}
      policyDescriptions={policyDescriptions}
    />)
  }, [showModal, refundDescription, policyDescriptions, timelineDeadline])

  const shieldIcon = refundableStatus === Refundable.Non
    ? <LineShieldSlashIcon />
    : <SolidShieldCheckIcon colour="highlight-primary-dark" />

  const cancellationLabel = useMemo<React.ReactNode>(() => {
    if (labelShape.kind === 'non-refundable') return refundDescription
    const styledRefund = <BodyText variant="medium" weight="bold" colour="highlight-primary-dark" as="span" wrap="no-wrap" key="refund">{refundDescription}</BodyText>
    return getPropertyCancellationLabelText({ intl, labelShape, refundType: styledRefund })
  }, [labelShape, intl, refundDescription])

  return <Clickable onClick={openModal}>
    <BodyTextBlock variant="medium" startIcon={shieldIcon}>
      <FormattedMessage
        id="aBR0Mwy9eVd8FXMnzQYq"
        defaultMessage="{cancellationLabel} <icon></icon>"
        description="Cancellation policy label with info icon in the cancellation flow"
        values={{
          cancellationLabel,
          icon: RENDER_INFO_ICON,
        }}
      />
    </BodyTextBlock>
  </Clickable>
}

export default PropertyCancellationPolicyView
